// Voice Mode 2.0 — P1: ordered PCM playback with instant barge-in.
//
// PCM arrives as small chunks (one per sentence, sometimes a few per sentence).
// Two rules make it sound continuous instead of stuttering:
//   1. **schedule ahead** — each buffer starts where the previous one ends
//      (`cursor`), so there is no gap between chunks;
//   2. **stop means stop** — barge-in tears the queue down in the same tick.

import { pcm16ToFloat32 } from './pcm';

export interface AudioBufferLike {
  readonly duration: number;
  copyToChannel(data: Float32Array, channel: number): void;
}

export interface AudioBufferSourceLike {
  buffer: AudioBufferLike | null;
  onended: ((ev?: unknown) => void) | null;
  connect(node: unknown): void;
  start(when?: number): void;
  stop(when?: number): void;
  disconnect(): void;
}

export interface AudioContextLike {
  readonly sampleRate: number;
  readonly currentTime: number;
  readonly destination: unknown;
  createBuffer(channels: number, length: number, sampleRate: number): AudioBufferLike;
  createBufferSource(): AudioBufferSourceLike;
  resume?(): Promise<void>;
  close?(): Promise<void>;
}

export type AudioContextFactory = () => AudioContextLike;

export interface PcmPlayerOptions {
  sampleRate?: number;
  contextFactory?: AudioContextFactory;
  /** Extra lead-in (seconds) so the very first chunk is scheduled, not late. */
  leadInSeconds?: number;
  /** Wall-clock source (ms). Injectable so clock liveness is testable. */
  now?: () => number;
  /**
   * Backlog (seconds of un-played audio) above which the context is treated as
   * suspect.  This is a MEMORY ceiling for a frozen context, not a reply limit
   * — see ``runaway``.  Default 45 s ≈ 2 MB of int16 @ 22 kHz.
   */
  maxQueuedSeconds?: number;
}

const MAX_QUEUED_SECONDS = 45;
/** Consecutive enqueues with a frozen play head before audio is abandoned. */
const STALL_CHECKS_TO_STOP = 3;
/**
 * Real elapsed time between play-head liveness samples. Below this the previous
 * verdict is reused, so a burst of enqueues in one tick cannot misjudge the clock.
 */
const CLOCK_SAMPLE_MS = 250;
/** The play head must cover at least this fraction of real elapsed time. */
const CLOCK_MIN_RATE = 0.5;

export class PcmPlayer {
  sampleRate: number;
  private readonly contextFactory: AudioContextFactory;
  private readonly leadIn: number;
  private ctx: AudioContextLike | null = null;
  private cursor = 0;
  private sources = new Set<AudioBufferSourceLike>();
  private lastStopAt = 0;
  private readonly maxQueuedSeconds: number;
  private readonly now: () => number;
  /** Play-head position at the previous queue check (stall detection). */
  private lastClock = 0;
  /** How many consecutive checks saw no play-head progress. */
  private stalledChecks = 0;
  /** Wall clock at the last play-head liveness sample. */
  private clockWallAt = 0;
  /** Audio clock at the last play-head liveness sample. */
  private clockAudioAt = 0;
  /** Whether any liveness sample has been taken yet. */
  private clockSampled = false;
  /** Most recent liveness verdict (reused between samples). */
  private clockAdvancing = true;

  constructor(options: PcmPlayerOptions = {}) {
    this.sampleRate = options.sampleRate ?? 22050;
    this.contextFactory =
      options.contextFactory ??
      (() => {
        const Ctor: typeof AudioContext | undefined =
          (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
            .AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) throw new Error('Web Audio is not available in this browser');
        return new Ctor() as unknown as AudioContextLike;
      });
    this.leadIn = options.leadInSeconds ?? 0.06;
    this.maxQueuedSeconds = options.maxQueuedSeconds ?? MAX_QUEUED_SECONDS;
    this.now = options.now ?? (() => Date.now());
  }

  /** Lazily create the AudioContext (must follow a user gesture on iOS/Safari). */
  private ensureContext(): AudioContextLike | null {
    if (this.ctx) return this.ctx;
    try {
      this.ctx = this.contextFactory();
    } catch {
      this.ctx = null;
    }
    return this.ctx;
  }

  async resume(): Promise<void> {
    const ctx = this.ensureContext();
    try {
      await ctx?.resume?.();
    } catch {
      /* autoplay policy: the next gesture will unlock it */
    }
  }

  get playing(): boolean {
    // True while scheduled audio is still ahead of the play head (real
    // playback), not merely because something was queued once.
    const ctx = this.ctx;
    if (!ctx) return false;
    if (this.sources.size === 0) return false;
    if (this.cursor <= ctx.currentTime + 0.02) return false;
    // The play head must also be MOVING.  On a suspended AudioContext (iOS
    // interruption, autoplay policy, silent switch) `currentTime` freezes, so
    // cursor stays ahead of it forever and this getter would report "still
    // playing" indefinitely.  The widget treats that as the assistant talking
    // and suppresses every transcript as echo — the owner's "it isn't picking
    // up anything I'm saying" (2026-09-21).  A frozen clock is not playback.
    return this.isClockAdvancing(ctx);
  }

  /**
   * Is the audio play head actually advancing in real time?
   *
   * Compared against a wall clock rather than by counting calls, because a
   * healthy reply fires several enqueues per tick while a stalled context fires
   * none — the failure is a play head that does not move, not a quiet moment.
   */
  private isClockAdvancing(ctx: AudioContextLike): boolean {
    const wall = this.now();
    if (!this.clockSampled) {
      this.clockSampled = true;
      this.clockWallAt = wall;
      this.clockAudioAt = ctx.currentTime;
      return true; // no interval to judge yet — assume healthy
    }
    const elapsedMs = wall - this.clockWallAt;
    if (elapsedMs < CLOCK_SAMPLE_MS) return this.clockAdvancing;
    const advanced = ctx.currentTime - this.clockAudioAt;
    this.clockWallAt = wall;
    this.clockAudioAt = ctx.currentTime;
    this.clockAdvancing = advanced >= (elapsedMs / 1000) * CLOCK_MIN_RATE;
    return this.clockAdvancing;
  }

  get queuedSeconds(): number {
    const ctx = this.ctx;
    if (!ctx) return 0;
    return Math.max(0, this.cursor - ctx.currentTime);
  }

  /**
   * True when the backlog of *un-played* audio exceeds the memory ceiling.
   *
   * This is deliberately NOT a "the reply is too long" test.  The TTS streams
   * one whole sentence of PCM as a single chunk, so a perfectly healthy reply
   * legitimately sits many seconds ahead of the speaker; the backlog only
   * shrinks as it plays.  Only a context whose play head has stopped moving can
   * never drain, so only that is pathological (see ``guardQueue``).
   *
   * 2026-09-20 bug: the guard summed every chunk ever enqueued and never
   * decayed, so ~12 s into any long reply it called stop() and killed the rest
   * of the answer mid-sentence ("he only read up the first part").
   * 2026-09-21 bug (owner, live): the ``queuedSeconds`` fix still compared the
   * *streaming* backlog against a 12 s cap, and sentence-level TTS arrives ~7 s
   * of audio at a time — so on a longer reply the backlog crossed 12 s while
   * audio was playing normally, stop() fired ~2.9 s in, and the reply died
   * right after "Quiet and healthy right now. You've got …".  Measured on the
   * production socket: 4 chunks / 17.3 s of audio / backlog peaked at 15.0 s.
   */
  get runaway(): boolean {
    const ctx = this.ctx;
    if (!ctx) return false;
    return this.queuedSeconds > this.maxQueuedSeconds;
  }

  /**
   * Only a FROZEN play head justifies dropping audio: a live context always
   * drains the backlog, however far ahead the server has streamed.  So we kill
   * the queue only after several consecutive checks show no progress *and* a
   * resume() attempt, which is the actual failure this guard exists for
   * (suspended/offline AudioContext on mobile).
   */
  private guardQueue(ctx: AudioContextLike): void {
    if (!this.runaway) {
      this.stalledChecks = 0;
      this.lastClock = ctx.currentTime;
      return;
    }
    // TTS chunks arrive seconds apart in production, so comparing the play head
    // between consecutive enqueues is a fair liveness test.  A suspended
    // context shows no advance at all and trips the guard.
    const progressed = ctx.currentTime - this.lastClock > 0.05;
    this.lastClock = ctx.currentTime;
    if (progressed) {
      // Backlog is large but draining: a long sentence ahead of the speaker is
      // normal. Never truncate for this.
      this.stalledChecks = 0;
      return;
    }
    this.stalledChecks += 1;
    void this.resume();
    if (this.stalledChecks >= STALL_CHECKS_TO_STOP) this.stop();
  }

  /** Queue one int16 PCM chunk for playback. */
  enqueue(pcm: Int16Array): void {
    if (!pcm.length) return;
    const ctx = this.ensureContext();
    if (!ctx) return;

    this.guardQueue(ctx);

    // Mature the play-head liveness sample on every chunk (result unused here;
    // the verdict is consumed by `playing`).  Without this, the first `playing`
    // check after a start would always bootstrap to "healthy".
    this.isClockAdvancing(ctx);

    const rate = this.sampleRate || ctx.sampleRate;
    const buffer = ctx.createBuffer(1, pcm.length, rate);
    buffer.copyToChannel(pcm16ToFloat32(pcm), 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = () => {
      this.sources.delete(source);
    };

    const startAt = Math.max(ctx.currentTime + this.leadIn, this.cursor || 0);
    try {
      source.start(startAt);
    } catch {
      return;
    }
    this.cursor = startAt + buffer.duration;
    this.sources.add(source);
  }
  /** Barge-in: silence immediately and forget everything queued. */
  stop(): void {
    const ctx = this.ctx;
    for (const source of this.sources) {
      try {
        source.onended = null;
        source.stop();
        source.disconnect();
      } catch {
        /* already finished */
      }
    }
    this.sources.clear();
    this.cursor = 0;
    this.stalledChecks = 0;
    // A fresh start gets a fresh liveness sample: stop() is also the recovery
    // path after a stalled context, and the new audio must be judged on its own.
    this.clockSampled = false;
    this.clockAdvancing = true;
    this.lastStopAt = ctx ? ctx.currentTime : 0;
  }

  /** Alias used by the session controller when playback should drain quietly. */
  flush(): void {
    this.stop();
  }

  async dispose(): Promise<void> {
    this.stop();
    const ctx = this.ctx;
    this.ctx = null;
    try {
      await ctx?.close?.();
    } catch {
      /* nothing to close */
    }
  }
}
